import { TemplateResult, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzGenericWidgetContent } from 'apps/webcomponents/src/genericDashboard/gzGenericWidget';
import {
  PhasrAttackSurfaceReductionPayload,
} from '@nirvana/shared/dtos/types/widgets/common';
import { FontSizes } from 'apps/webcomponents/src/types/common/types';
import { WidgetStateFrontEnd } from 'apps/webcomponents/src/genericDashboard/gzGenericWidget/types';
import { WEB_COMPONENT_ATTACK_SURFACE_REDUCTION_POTENTIAL } from 'apps/webcomponents/src/webcomponents.constants';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';

@customElement(WEB_COMPONENT_ATTACK_SURFACE_REDUCTION_POTENTIAL)
export class AttackSurfaceReductionPotentialWebComponent extends GzGenericWidgetContent {
  @property({type: Object, attribute: true, reflect: true})
  payload: PhasrAttackSurfaceReductionPayload;

  readonly activeGaugeColors = ['--green-layout-100-primary-green', '--yellow-alert-050', '--red-alert-500-severe'];
  readonly colorGradientOffsets = [0, 15, 100];

  protected renderContent(): TemplateResult {
    return html`
      <div class="d-flex align-items-center mt-4">
        ${this.renderGauge(this.isEmptyData())}${this.renderDescription()}
      </div>
      ${this.renderGatheringDataInfo()}`;
  }

  private renderGatheringDataInfo(): TemplateResult {
    if (this.payload?.learning === undefined || this.state === WidgetStateFrontEnd.IS_ERROR) {
      return html``;
    }

    const targetUsersCount = this.payload.learning?.targetUsersCount;
    const remainingDays = this.payload?.learning?.estimatedRemainingDays;
    const isLearning = this.payload?.isLearning;

    if (!targetUsersCount) {
      return html``;
    }

    return html`
      <phasr-gathering-data-info
        nrUsers="${targetUsersCount}"
        remainingDays="${remainingDays}"
        .isLearning="${isLearning}"
        .showRemainingDays="${!this.payload?.surfacePercentageReduction}"
        class="mt-3"
      ></phasr-gathering-data-info`;
  }

  protected isEmptyData(): boolean {
    return (
      this.state === WidgetStateFrontEnd.IS_ERROR ||
      this.state === WidgetStateFrontEnd.IS_EMPTY ||
      (this.state === WidgetStateFrontEnd.IS_ACTIVE && !this.payload?.surfacePercentageReduction)
    );
  }

  private renderGauge(disabled: boolean = false): TemplateResult {
    const percentage = this.payload?.surfacePercentageReduction;
    if (!percentage) {
      disabled = true;
    }

    return html`
      <gz-gauge
        .colors="${this.activeGaugeColors}"
        .gradientOffsets="${this.colorGradientOffsets}"
        value="${disabled ? 0 : percentage}"
        class="mr-4"
        ?disabled="${disabled}"
        hasLabels
      ></gz-gauge>`;
  }

  private renderDescription(): TemplateResult {
    return html`
      <div class="description-container">
        <gz-text fontsize="${FontSizes.HEADING_6}"
        >${translateFn(
          `This widget shows the company's current attack surface exposure, indicating the percentage of potentially exploitable attack vectors. The goal is to minimize exposure as much as possible.`
        )}
        </gz-text
        >
        <gz-text fontsize="${FontSizes.HEADING_6}">
          <a href="https://www.bitdefender.com/business/support/en/77209-1192265-exploring-widgets.html" target="_blank">${translateFn('Learn more')}</a>
        </gz-text>
      </div>`;
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="d-flex align-items-center mt-4">
        ${this.renderGauge(true)}
        <div class="loading-section">
          <gz-loader rows="4"></gz-loader>
        </div>
      </div>
    `;
  }

  static get styles() {
    return styles;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'attack-surface-reduction-potential': AttackSurfaceReductionPotentialWebComponent;
  }
}
